import { Reader, stringAt } from "./reader.ts";

export const EM_XTENSA = 94;
export const EM_RISCV = 243;

const SHT_SYMTAB = 2;
const SHT_NOBITS = 8;
const SHF_EXECINSTR = 0x4;
const SHF_COMPRESSED = 0x800;
const STT_FUNC = 2;

export interface Section {
    name: string;
    type: number;
    flags: number;
    address: number;
    offset: number;
    size: number;
    link: number;
}

interface FunctionSymbol {
    address: number;
    size: number;
    name: string;
}

/**
 * The parts of a 32-bit little-endian ELF file that decoding needs: the
 * section table, the executable address ranges and the function symbols.
 */
export class Elf {
    readonly bytes: Uint8Array;
    readonly machine: number;
    readonly sections: Section[];
    readonly #executable: [number, number][];
    #functions: FunctionSymbol[] | undefined;

    constructor(bytes: Uint8Array) {
        if (bytes[0] !== 0x7f || bytes[1] !== 0x45 || bytes[2] !== 0x4c || bytes[3] !== 0x46) {
            throw new Error("not an ELF file");
        }
        if (bytes[4] !== 1 || bytes[5] !== 1) {
            throw new Error("not a 32-bit little-endian ELF file");
        }
        this.bytes = bytes;
        const header = new Reader(bytes, 18);
        this.machine = header.u16();
        header.offset = 32;
        const shoff = header.u32();
        header.offset = 46;
        const shentsize = header.u16();
        const shnum = header.u16();
        const shstrndx = header.u16();

        const raw: Omit<Section, "name">[] = [];
        const names: number[] = [];
        for (let i = 0; i < shnum; i++) {
            const r = new Reader(bytes, shoff + i * shentsize);
            names.push(r.u32());
            const type = r.u32();
            const flags = r.u32();
            const address = r.u32();
            const offset = r.u32();
            const size = r.u32();
            const link = r.u32();
            raw.push({ type, flags, address, offset, size, link });
        }
        const strtab = raw[shstrndx];
        this.sections = raw.map((section, i) => ({
            ...section,
            name: strtab === undefined ? "" : stringAt(this.#contents(strtab), names[i]!),
        }));
        this.#executable = this.sections
            .filter((s) => (s.flags & SHF_EXECINSTR) !== 0 && s.size > 0)
            .map((s): [number, number] => [s.address, s.address + s.size])
            .sort((a, b) => a[0] - b[0]);
    }

    /** A section's bytes, or undefined if the file has no such section. */
    section(name: string): Uint8Array | undefined {
        const section = this.sections.find((s) => s.name === name);
        if (section === undefined || section.type === SHT_NOBITS) return undefined;
        if ((section.flags & SHF_COMPRESSED) !== 0) {
            throw new Error(`${name} is compressed, which is not supported`);
        }
        return this.#contents(section);
    }

    /** True if the address is in a section that holds code. */
    isExecutable(address: number): boolean {
        for (const [start, end] of this.#executable) {
            if (address < start) return false;
            if (address < end) return true;
        }
        return false;
    }

    /** The function symbol covering an address, for code that has no debug info. */
    functionAt(address: number): string | undefined {
        const functions = (this.#functions ??= this.#readFunctions());
        let low = 0;
        let high = functions.length;
        while (low < high) {
            const mid = (low + high) >>> 1;
            if (functions[mid]!.address <= address) low = mid + 1;
            else high = mid;
        }
        const symbol = functions[low - 1];
        if (symbol === undefined) return undefined;
        const end = symbol.address + Math.max(symbol.size, 1);
        return address < end ? symbol.name : undefined;
    }

    /** Where a function starts, by its symbol name. */
    addressOf(name: string): number | undefined {
        const functions = (this.#functions ??= this.#readFunctions());
        return functions.find((symbol) => symbol.name === name)?.address;
    }

    #contents(section: Omit<Section, "name">): Uint8Array {
        return this.bytes.subarray(section.offset, section.offset + section.size);
    }

    #readFunctions(): FunctionSymbol[] {
        const symtab = this.sections.find((s) => s.type === SHT_SYMTAB);
        const strtab = symtab === undefined ? undefined : this.sections[symtab.link];
        if (symtab === undefined || strtab === undefined) return [];
        const names = this.#contents(strtab);
        const r = new Reader(this.#contents(symtab));
        const functions: FunctionSymbol[] = [];
        while (!r.done) {
            const name = r.u32();
            const address = r.u32();
            const size = r.u32();
            const info = r.u8();
            r.skip(3);
            if ((info & 0xf) === STT_FUNC && address !== 0) {
                functions.push({ address, size, name: stringAt(names, name) });
            }
        }
        return functions.sort((a, b) => a.address - b.address);
    }
}
